import { AsyncResult, Action, ApiAction } from '../../../index';
import api from '../../../redux-api';
import { addToArray, removeFromArray, updateInArray } from '../../../../utils/immutability';
import { ReduxState } from '../../types';

import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import EnterpriseGroup from '@/types/enterprise-group';
import EnterpriseGroupInfos from '@/types/enterprise-group-infos';
import EnterpriseMember from '@/types/enterprise-member';

const ADD_ENTERPRISE_GROUP: ApiAction = {
  type: 'connect/enterprises/add-group',
  endpoint: 'enterprises/:id/groups',
};

const LOAD_ENTERPRISE_GROUPS: ApiAction = {
  type: 'connect/enterprises/add-group',
  endpoint: 'enterprises/:id/groups',
};

const SAVE_ENTERPRISE_GROUP_INFO: ApiAction = {
  type: 'connect/enterprises/save-group-info',
  endpoint: 'enterprises/:id/groups/:groupId/',
};

const DELETE_ENTERPRISE_GROUP: ApiAction = {
  type: 'connect/enterprises/delete-group',
  endpoint: 'enterprises/:id/groups/:groupId/',
};

const ADD_ENTERPRISE_GROUP_MEMBER: ApiAction = {
  type: 'connect/enterprises/add-group-member',
  endpoint: 'enterprises/:id/groups/:groupId/members',
};

const REMOVE_ENTERPRISE_GROUP_MEMBER: ApiAction = {
  type: 'connect/enterprises/remove-group-member',
  endpoint: 'enterprises/:id/groups/:groupId/members/:userId/',
};

// Reducer
export default function reducer(state: EnterpriseAdminDetails, action: Action): EnterpriseAdminDetails {
  if (state === null) {
    return null;
  }

  switch (action.type) {
    case `${ADD_ENTERPRISE_GROUP.type}_SUCCESS`: {
      return {
        ...state,
        groups: addToArray<EnterpriseGroup>(state.groups, action.data),
      };
    }

    case `${SAVE_ENTERPRISE_GROUP_INFO.type}_SUCCESS`: {
      return {
        ...state,
        groups: updateInArray<EnterpriseGroup>(state.groups, action.data.group),
      };
    }

    // TODO -- Optimistic UI would be to remove it on action and put it back on FAILURE
    case `${DELETE_ENTERPRISE_GROUP.type}_SUCCESS`: {
      return {
        ...state,
        groups: removeFromArray(state.groups, action.data.groupId),
      };
    }

    case `${ADD_ENTERPRISE_GROUP_MEMBER.type}_SUCCESS`: {
      const group = state.groups.find(g => g._id === action.params.groupId);

      if (!group) {
        return state;
      }

      const newGroup = { ...group, members: addToArray(group.members, action.data) };

      return {
        ...state,
        groups: updateInArray(state.groups, newGroup),
      };
    }

    // TODO -- Optimistic UI would be to remove it on action and put it back on FAILURE
    case `${REMOVE_ENTERPRISE_GROUP_MEMBER.type}_SUCCESS`: {
      const group = state.groups.find(g => g._id === action.params.groupId);

      if (!group) {
        return state;
      }

      const newGroup = { ...group, members: removeFromArray(group.members, action.params.userId) };

      return {
        ...state,
        groups: updateInArray(state.groups, newGroup),
      };
    }

    default: {
      return state;
    }
  }
}

const getCurrentEnterpriseId = (state: ReduxState) => {
  const { details } = state.enterprise;
  return details ? details._id : null;
};

export const saveEnterpriseGroupInfos =
  (groupId: string, infos: EnterpriseGroupInfos): AsyncResult<EnterpriseGroup> =>
  (dispatch, getState) => {
    const id = getCurrentEnterpriseId(getState());
    return api.patch(SAVE_ENTERPRISE_GROUP_INFO, { id, groupId }, infos).then((res: { group: any }) => res.group);
  };

export const addEnterpriseGroupMember =
  (groupId: string, userId: string): AsyncResult<EnterpriseMember> =>
  (dispatch, getState) => {
    const id = getCurrentEnterpriseId(getState());
    return api.post(ADD_ENTERPRISE_GROUP_MEMBER, { id, groupId }, { userId });
  };

export const deleteEnterpriseGroupMember =
  (groupId: string, userId: string): AsyncResult<void> =>
  (dispatch, getState) => {
    const id = getCurrentEnterpriseId(getState());
    return api.delete(REMOVE_ENTERPRISE_GROUP_MEMBER, { id, groupId, userId });
  };

export const addEnterpriseGroup =
  (name: string): AsyncResult<EnterpriseGroup> =>
  (dispatch, getState) => {
    const id = getCurrentEnterpriseId(getState());
    return api.post(ADD_ENTERPRISE_GROUP, { id }, { name });
  };

export const deleteEnterpriseGroup =
  (groupId: string): AsyncResult<void> =>
  (dispatch, getState) => {
    const id = getCurrentEnterpriseId(getState());
    return api.delete(DELETE_ENTERPRISE_GROUP, { id, groupId });
  };

export const loadEnterpriseGroups = (): AsyncResult<EnterpriseGroup[]> => (dispatch, getState) => {
  const id = getCurrentEnterpriseId(getState());
  return api.get(LOAD_ENTERPRISE_GROUPS, { id });
};
